import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_BASE });

// Attach token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = (params = {}) => api.get('/users', { params });
export const createUser = (data) => api.post('/users', data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const changeOwnPassword = (currentPassword, newPassword) => api.put('/users/me/password', { currentPassword, newPassword });
export const adminChangePassword = (userId, newPassword) => api.put(`/users/${userId}/password`, { newPassword });
export const changeUserRole = (userId, role) => api.put(`/users/${userId}/role`, { role });
export const adminEditUser = (userId, data) => api.put(`/users/${userId}/edit`, data);
export const updateUserPermissions = (userId, permissions) => api.put(`/users/${userId}/permissions`, { permissions });
export const updateProfile = (data) => api.put('/users/me/profile', data);

// Organizations
export const getOrganizations = () => api.get('/organizations');
export const getOrganization = (id) => api.get(`/organizations/${id}`);
export const createOrganization = (data) => api.post('/organizations', data);
export const updateOrganization = (id, data) => api.put(`/organizations/${id}`, data);
export const deleteOrganization = (id) => api.delete(`/organizations/${id}`);
export const addMember = (orgId, userId) => api.post(`/organizations/${orgId}/members`, { userId });
export const removeMember = (orgId, userId) => api.delete(`/organizations/${orgId}/members/${userId}`);
export const getOrgMembers = (orgId) => api.get(`/organizations/${orgId}/members`);

// Events
export const getOrgEvents = (orgId) => api.get(`/events/org/${orgId}`);
export const getEvent = (id) => api.get(`/events/${id}`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);
export const rsvpEvent = (id, status) => api.post(`/events/${id}/rsvp`, { status });

// Feedbacks
export const getEventFeedbacks = (eventId) => api.get(`/feedbacks/event/${eventId}`);
export const getOrgFeedbacks = (orgId) => api.get(`/feedbacks/org/${orgId}`);
export const createFeedback = (data) => api.post('/feedbacks', data);

// Boards
export const getOrgBoards = (orgId, scope = 'club') => api.get(`/boards/org/${orgId}`, { params: { scope } });
export const getBoard = (id) => api.get(`/boards/${id}`);
export const createBoard = (data) => api.post('/boards', data);
export const updateBoard = (id, data) => api.put(`/boards/${id}`, data);
export const deleteBoard = (id) => api.delete(`/boards/${id}`);

// Logs
export const getLogs = () => api.get('/logs');
export const clearLogs = () => api.delete('/logs');

// Notifications
export const getMyNotifications = () => api.get('/notifications/my');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Documents
export const getOrgDocuments = (orgId, scope = 'club') => api.get(`/documents/org/${orgId}`, { params: { scope } });
export const saveDocument = (data) => api.post('/documents', data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);

// Notes
export const getMemberNotes = (memberId) => api.get(`/notes/${memberId}`);
export const getOrgNotes = (orgId) => api.get(`/notes/org/${orgId}`);
export const createNote = (data) => api.post('/notes', data);
export const deleteNote = (id) => api.delete(`/notes/${id}`);

// Activity
export const sendHeartbeat = () => api.post('/activity/heartbeat');
export const getActivityStats = () => api.get('/activity/stats');

// Outfits
export const getOrgOutfits = (orgId) => api.get(`/outfits/org/${orgId}`);
export const saveOutfitItem = (orgId, data) => api.post(`/outfits/org/${orgId}`, data);
export const deleteOutfitItem = (id) => api.delete(`/outfits/${id}`);

// Discord
export const getDiscordSettings = (orgId) => api.get(`/discord/org/${orgId}`);
export const saveDiscordSettings = (orgId, data) => api.put(`/discord/org/${orgId}`, data);

// Discord Presets (Admin)
export const getDiscordPresets = () => api.get('/discord/presets');
export const createDiscordPreset = (data) => api.post('/discord/presets', data);
export const updateDiscordPreset = (id, data) => api.put(`/discord/presets/${id}`, data);
export const deleteDiscordPreset = (id) => api.delete(`/discord/presets/${id}`);
export const sendDiscordPreset = (id) => api.post(`/discord/presets/${id}/send`);
export const sendCustomEmbed = (data) => api.post('/discord/send-custom-embed', data);

export default api;
